import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { PUBLICATION_LIFECYCLE_KIND_OPTIONS } from '../../model/options';
import type {
  PublicationCatalog,
  PublicationCatalogLifecycleKind,
  PublicationCatalogType,
} from '../../model/publicationTypes';

export interface PublicationCatalogFormValue {
  code: string;
  name: string;
  description: string;
  lifecycleKind: PublicationCatalogLifecycleKind;
  catalogTypeCode: string;
  validFrom: string;
  validTo: string;
}

interface PublicationCatalogFormDialogProps {
  open: boolean;
  /** The catalog being edited, or `null` to create. */
  editing: PublicationCatalog | null;
  catalogTypes: PublicationCatalogType[];
  saving: boolean;
  onClose: () => void;
  onSubmit: (value: PublicationCatalogFormValue) => void;
}

const EMPTY_VALUE: PublicationCatalogFormValue = {
  code: '',
  name: '',
  description: '',
  lifecycleKind: 'PERMANENT',
  catalogTypeCode: '',
  validFrom: '',
  validTo: '',
};

/** `YYYY-MM-DDTHH:mm` (local) <-> ISO, matching `RuleFormDialog`'s existing datetime-local convention. */
function toIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toLocal(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

/**
 * Create / edit a publication catalog. On edit, `code`/`lifecycleKind`/`catalogTypeCode`/
 * `validFrom`/`validTo`/`status` are immutable (per `UpdatePublicationCatalogRequest` — only
 * name/description can be edited here); lifecycle transitions use the dedicated row actions
 * instead (see `PublicationCatalogsPanel`).
 */
export function PublicationCatalogFormDialog({
  open,
  editing,
  catalogTypes,
  saving,
  onClose,
  onSubmit,
}: PublicationCatalogFormDialogProps) {
  const { t } = useTranslation('catalog');
  const isEdit = editing !== null;
  const [value, setValue] = useState<PublicationCatalogFormValue>(EMPTY_VALUE);

  useEffect(() => {
    if (!open) return;
    if (!editing) {
      setValue(EMPTY_VALUE);
      return;
    }
    setValue({
      code: editing.code,
      name: editing.name,
      description: editing.description ?? '',
      lifecycleKind: editing.lifecycleKind,
      catalogTypeCode: editing.catalogTypeCode,
      validFrom: toLocal(editing.validFrom),
      validTo: toLocal(editing.validTo),
    });
  }, [open, editing]);

  const isTemporary = value.lifecycleKind === 'TEMPORARY';
  const valid =
    value.name.trim() !== '' &&
    (isEdit || (value.code.trim() !== '' && value.catalogTypeCode !== '')) &&
    (!isTemporary || (value.validFrom !== '' && value.validTo !== ''));

  const submit = () => {
    if (!valid) return;
    onSubmit({ ...value, validFrom: toIso(value.validFrom) ?? '', validTo: toIso(value.validTo) ?? '' });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit
          ? t('publicationCatalogsPanel.formDialog.editTitle')
          : t('publicationCatalogsPanel.formDialog.newTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label={t('publicationCatalogsPanel.formDialog.codeLabel')}
            required
            value={value.code}
            onChange={(e) => setValue((v) => ({ ...v, code: e.target.value }))}
            error={!isEdit && value.code.trim() === ''}
            disabled={isEdit}
            fullWidth
            autoFocus={!isEdit}
            slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
          />
          <TextField
            label={t('publicationCatalogsPanel.formDialog.nameLabel')}
            required
            value={value.name}
            onChange={(e) => setValue((v) => ({ ...v, name: e.target.value }))}
            error={value.name.trim() === ''}
            fullWidth
            autoFocus={isEdit}
          />
          <TextField
            label={t('publicationCatalogsPanel.formDialog.descriptionLabel')}
            value={value.description}
            onChange={(e) => setValue((v) => ({ ...v, description: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            select
            label={t('publicationCatalogsPanel.formDialog.catalogTypeLabel')}
            required
            value={value.catalogTypeCode}
            onChange={(e) => setValue((v) => ({ ...v, catalogTypeCode: e.target.value }))}
            error={!isEdit && value.catalogTypeCode === ''}
            disabled={isEdit}
            fullWidth
          >
            {catalogTypes.map((ct) => (
              <MenuItem key={ct.code} value={ct.code}>
                {ct.displayName} ({ct.code})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('publicationCatalogsPanel.formDialog.lifecycleKindLabel')}
            value={value.lifecycleKind}
            onChange={(e) =>
              setValue((v) => ({
                ...v,
                lifecycleKind: e.target.value as PublicationCatalogLifecycleKind,
              }))
            }
            disabled={isEdit}
            fullWidth
          >
            {PUBLICATION_LIFECYCLE_KIND_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.label)}
              </MenuItem>
            ))}
          </TextField>
          {isTemporary ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
              <TextField
                label={t('publicationCatalogsPanel.formDialog.validFromLabel')}
                type="datetime-local"
                required
                value={value.validFrom}
                onChange={(e) => setValue((v) => ({ ...v, validFrom: e.target.value }))}
                error={!isEdit && value.validFrom === ''}
                disabled={isEdit}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label={t('publicationCatalogsPanel.formDialog.validToLabel')}
                type="datetime-local"
                required
                value={value.validTo}
                onChange={(e) => setValue((v) => ({ ...v, validTo: e.target.value }))}
                error={!isEdit && value.validTo === ''}
                disabled={isEdit}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
          ) : null}
          {isEdit ? (
            <Alert severity="info" variant="outlined">
              {t('publicationCatalogsPanel.formDialog.immutableHint')}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
